/**
 * StreakTile Component
 * Read-only streak display tile for coach and guardian views
 * Shows streak progress without action buttons
 */

import PropTypes from 'prop-types'
import { Calendar } from 'lucide-react'

/**
 * StreakTile - Read-only companion card for ActivityHeatmap
 * Displays streak information for coaches and guardians viewing student progress
 *
 * @param {Object} props - Component props
 * @param {Object} props.stats - Activity stats from useActivityHeatmap or progressData
 * @param {string} props.className - Additional CSS classes
 */
function StreakTile({ stats = {}, className = '' }) {
  const {
    currentStreak = 0,
    longestStreak = 0,
    totalActiveDays = 0,
    isActiveToday = false
  } = stats

  // Calculate progress percentage toward longest streak or next milestone
  const nextMilestone = currentStreak < 7 ? 7 : currentStreak < 14 ? 14 : currentStreak < 30 ? 30 : currentStreak < 60 ? 60 : currentStreak < 100 ? 100 : 365
  const progressTarget = longestStreak > currentStreak ? longestStreak : nextMilestone
  const progress = progressTarget > 0 ? Math.min((currentStreak / progressTarget) * 100, 100) : 0

  // Determine styling based on streak state
  const getCardConfig = () => {
    if (currentStreak >= longestStreak && currentStreak > 0 && longestStreak > 0) {
      return {
        badgeBg: 'bg-gradient-to-r from-purple-100 to-pink-100 dark:from-purple-900/30 dark:to-pink-900/30',
        badgeText: 'text-purple-700 dark:text-purple-400',
        progressBg: 'bg-gradient-to-r from-purple-400 to-pink-500'
      }
    }
    if (isActiveToday) {
      return {
        badgeBg: 'bg-gradient-to-r from-green-100 to-emerald-100 dark:from-green-900/30 dark:to-emerald-900/30',
        badgeText: 'text-green-700 dark:text-green-400',
        progressBg: 'bg-gradient-to-r from-green-400 to-emerald-500'
      }
    }
    if (currentStreak > 0) {
      return {
        badgeBg: 'bg-gradient-to-r from-orange-100 to-amber-100 dark:from-orange-900/30 dark:to-amber-900/30',
        badgeText: 'text-orange-700 dark:text-orange-400',
        progressBg: 'bg-gradient-to-r from-orange-400 to-red-500'
      }
    }
    return {
      badgeBg: 'bg-gray-100 dark:bg-gray-800',
      badgeText: 'text-gray-600 dark:text-gray-400',
      progressBg: 'bg-gray-300 dark:bg-gray-600'
    }
  }

  const config = getCardConfig()

  return (
    <div className={`card ${className}`}>
      {/* Card Header - matches ActivityHeatmap structure */}
      <div className="card-header">
        <div className="flex items-center gap-2">
          <Calendar className="w-5 h-5 text-primary-600 flex-shrink-0" />
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            Activity Summary
          </h3>
        </div>
      </div>

      {/* Card Body */}
      <div className="card-body space-y-4">
        {/* Streak progress visualization */}
        <div className={`p-3 rounded-lg ${config.badgeBg}`}>
          <div className="flex items-center justify-between mb-2">
            <span className={`text-sm font-medium ${config.badgeText}`}>
              {currentStreak > 0 ? `${currentStreak} day streak` : 'No active streak'}
            </span>
            {longestStreak > 0 && (
              <span className="text-xs text-gray-500 dark:text-gray-400">
                Best: {longestStreak}
              </span>
            )}
          </div>
          {/* Progress bar */}
          <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2 overflow-hidden">
            <div
              className={`h-full rounded-full transition-all duration-500 ${config.progressBg}`}
              style={{ width: `${progress}%` }}
            />
          </div>
          {currentStreak > 0 && currentStreak < nextMilestone && (
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1.5">
              {nextMilestone - currentStreak} more {nextMilestone - currentStreak === 1 ? 'day' : 'days'} to {nextMilestone}-day milestone
            </p>
          )}
        </div>

        {/* Stats row */}
        <div className="grid grid-cols-2 gap-3">
          <div className="text-center p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-100 dark:border-gray-700">
            <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
              {totalActiveDays}
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Active Days
            </p>
          </div>
          <div className="text-center p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-100 dark:border-gray-700">
            <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
              {longestStreak}
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Best Streak
            </p>
          </div>
        </div>

        {/* Active today indicator */}
        {isActiveToday && (
          <div className="flex items-center gap-2 text-sm text-green-600 dark:text-green-400">
            <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
            <span>Practiced today</span>
          </div>
        )}
      </div>
    </div>
  )
}

StreakTile.propTypes = {
  /** Activity stats from useActivityHeatmap or progressData.activityHeatmap.stats */
  stats: PropTypes.shape({
    currentStreak: PropTypes.number,
    longestStreak: PropTypes.number,
    totalActiveDays: PropTypes.number,
    isActiveToday: PropTypes.bool
  }),
  /** Additional CSS classes */
  className: PropTypes.string
}

export default StreakTile
